import type { FormEvent } from 'react';
import { memo, useState, useCallback } from 'react';
import { Stack, Button } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { AppTextInput, AppTextarea } from '@/components/common';
import {
  WORKSPACE_NAME_MIN_LENGTH,
  WORKSPACE_NAME_MAX_LENGTH,
  WORKSPACE_DESCRIPTION_MAX_LENGTH,
} from '@/constants/workspace';
import { TEXTAREA_MAX_ROWS, TEXTAREA_MIN_ROWS } from '@/constants/ui';
import { useWorkspaceForm } from '@/hooks/useWorkspaceForm';
import type { PendingInvitation, WorkspaceRole } from '@/types/workspace';
import { InvitationFormSection } from './components/InvitationFormSection';
import styles from './WorkspaceCreationForm.module.css';

export interface WorkspaceCreationFormData {
  name: string;
  description?: string;
  invitations: PendingInvitation[];
}

interface WorkspaceCreationFormProps {
  onSubmit: (data: WorkspaceCreationFormData) => Promise<void>;
  isSubmitting?: boolean;
  initialValues?: { name?: string; description?: string };
  className?: string;
}

export const WorkspaceCreationForm = memo(function WorkspaceCreationForm({
  onSubmit,
  isSubmitting = false,
  initialValues,
  className,
}: WorkspaceCreationFormProps) {
  const { t } = useTranslation();

  const {
    name,
    description,
    errors,
    validateForm,
    handleNameChange,
    handleNameBlur,
    handleDescriptionChange,
  } = useWorkspaceForm({
    initialName: initialValues?.name,
    initialDescription: initialValues?.description,
  });

  const [pendingInvitations, setPendingInvitations] = useState<
    PendingInvitation[]
  >([]);
  const [isInvitationListExpanded, setIsInvitationListExpanded] =
    useState(false);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      if (!validateForm()) {
        return;
      }

      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
        invitations: pendingInvitations,
      });
    },
    [name, description, pendingInvitations, validateForm, onSubmit]
  );

  const handleAddInvitation = useCallback(
    (invitation: Omit<PendingInvitation, 'id'>) => {
      setPendingInvitations((prev) => [
        ...prev,
        { ...invitation, id: crypto.randomUUID() },
      ]);
    },
    []
  );

  const handleRemoveInvitation = useCallback((id: string) => {
    setPendingInvitations((prev) =>
      prev.filter((invitation) => invitation.id !== id)
    );
  }, []);

  const handleInvitationRoleChange = useCallback(
    (id: string, newRole: WorkspaceRole) => {
      setPendingInvitations((prev) =>
        prev.map((invitation) =>
          invitation.id === id ? { ...invitation, role: newRole } : invitation
        )
      );
    },
    []
  );

  const handleToggleExpanded = useCallback(() => {
    setIsInvitationListExpanded((prev) => !prev);
  }, []);

  const isFormValid =
    name.trim().length >= WORKSPACE_NAME_MIN_LENGTH &&
    !errors.name &&
    !errors.description;

  return (
    <form onSubmit={handleSubmit} className={className}>
      <div className={styles.formContent}>
        {!isInvitationListExpanded && (
          <Stack gap="md">
            <AppTextInput
              label={t('workspace.form.name')}
              placeholder={t('workspace.form.namePlaceholder')}
              value={name}
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              error={errors.name}
              maxLength={WORKSPACE_NAME_MAX_LENGTH}
              required
              disabled={isSubmitting}
            />

            <AppTextarea
              label={t('workspace.form.description')}
              placeholder={t('workspace.form.descriptionPlaceholder')}
              value={description}
              onChange={handleDescriptionChange}
              error={errors.description}
              maxLength={WORKSPACE_DESCRIPTION_MAX_LENGTH}
              minRows={TEXTAREA_MIN_ROWS}
              maxRows={TEXTAREA_MAX_ROWS}
              autosize
              disabled={isSubmitting}
            />
          </Stack>
        )}

        <InvitationFormSection
          pendingInvitations={pendingInvitations}
          onAdd={handleAddInvitation}
          onRemove={handleRemoveInvitation}
          onRoleChange={handleInvitationRoleChange}
          isSubmitting={isSubmitting}
          isExpanded={isInvitationListExpanded}
          onToggleExpanded={handleToggleExpanded}
        />

        {!isInvitationListExpanded && (
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={!isFormValid}
            leftSection={<IconCheck size={16} />}
            size="lg"
            fullWidth
            className={styles.submitButton}
          >
            {t('workspace.form.create')}
          </Button>
        )}
      </div>
    </form>
  );
});
